import { CheckCircle2, AlertTriangle } from 'lucide-react';
import type { LatestRelease } from './api';

/*
 * Edge status presentation helpers shared by the fleet (EdgesPage), edge
 * detail (EdgeDetailPage) and home detail (HomeDetailPage) views. These used
 * to be copy-pasted per page, which let thresholds and formats drift.
 */

export const GITHUB_REPO = 'drabindr/manor';

export function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const deltaSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const m = Math.round(deltaSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function heartbeatColor(iso?: string): string {
  if (!iso) return 'bg-slate-400';
  const ageSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (ageSec < 10 * 60) return 'bg-emerald-500';
  if (ageSec < 60 * 60) return 'bg-amber-500';
  return 'bg-red-500';
}

const METRICS_STALE_SEC = 15 * 60;

export function isMetricsStale(iso?: string): boolean {
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;
  return (Date.now() - t) / 1000 >= METRICS_STALE_SEC;
}

export function shaUrl(sha: string | undefined): string | undefined {
  if (!sha) return undefined;
  return `https://github.com/${GITHUB_REPO}/commit/${sha}`;
}

function FallbackBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500 uppercase tracking-wider">
      {children}
    </span>
  );
}

/** Is this edge's deployed SHA current with the latest published release? */
export function DriftBadge({ edgeSha, latest }: { edgeSha: string | undefined; latest: LatestRelease | null }) {
  if (!latest?.manifest?.sha) {
    return <FallbackBadge>no release info</FallbackBadge>;
  }
  if (!edgeSha) {
    return <FallbackBadge>unknown</FallbackBadge>;
  }
  if (edgeSha === latest.manifest.sha) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 uppercase tracking-wider"
        title={`Channel ${latest.channel} is at ${latest.manifest.shaShort}`}
      >
        <CheckCircle2 size={10} /> up to date
      </span>
    );
  }
  return (
    <a
      href={`https://github.com/${GITHUB_REPO}/compare/${edgeSha}...${latest.manifest.sha}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 uppercase tracking-wider hover:bg-amber-100"
      title={`Latest on ${latest.channel}: ${latest.manifest.shaShort} — ${latest.manifest.subject}`}
    >
      <AlertTriangle size={10} /> behind {latest.manifest.shaShort}
    </a>
  );
}
